import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PageTitle from '../../Component/Comman/PageTitle';
import { getTransactions } from '../../services/paymentService';

function TransactionPage({ auth }) {
    const hasFetched = useRef(false);
    const [loading, setLoading] = useState(true);
    const [transactions, setTransactions] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [summary, setSummary] = useState(null);
    const [filters, setFilters] = useState({
        type: '',
        status: '',
        startDate: null,
        endDate: null,
        page: 1,
        limit: 10,
        sortBy: 'createdAt',
        sortOrder: 'desc'
    });

    useEffect(() => {
        if (!hasFetched.current && auth?.isAuthenticated) {
            hasFetched.current = true;
            fetchTransactions();
        }
    }, [auth]);

    useEffect(() => {
        if (hasFetched.current) {
            fetchTransactions();
        }
    }, [filters]);

    const fetchTransactions = async () => {
        try {
            setLoading(true);
            
            // Prepare filter params
            const params = { ...filters };
            if (params.startDate) {
                params.startDate = params.startDate.toISOString().split('T')[0];
            }
            if (params.endDate) {
                params.endDate = params.endDate.toISOString().split('T')[0];
            }

            const response = await getTransactions(params);
            
            if (response.success) {
                setTransactions(response.data?.transactions || []);
                setPagination(response.data?.pagination || {});
                setSummary(response.data?.summary || null);
            }
        } catch (error) {
            console.error('Error fetching transactions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value,
            page: 1 // Reset page when filter changes
        }));
    };

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setFilters(prev => ({
            ...prev,
            startDate: start,
            endDate: end,
            page: 1
        }));
    };

    const handlePageChange = (page) => {
        setFilters(prev => ({
            ...prev,
            page
        }));
    };

    const getStatusBadge = (status) => {
        const variants = {
            pending: 'warning',
            completed: 'success',
            failed: 'danger',
            rejected: 'danger',
            cancelled: 'secondary'
        };
        const bgClass = variants[status?.toLowerCase()] || 'secondary';
        return <span className={`badge bg-${bgClass}`}>{status?.toUpperCase() || 'N/A'}</span>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };

    return (
        <>
            <div className="body-header border-bottom d-flex py-3 mb-3">
                <PageTitle pagetitle='Transaction History' sidebutton={false} />
            </div>
            <div className="container-xxl">
                {/* Summary Cards */}
                {summary && (
                    <div className="row g-3 mb-4">
                        <div className="col-lg-6 col-md-6 col-sm-12">
                            <div className="card h-100 stats-card stats-card-success">
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <div className="text-muted small text-uppercase mb-2 fw-semibold">Total Deposits</div>
                                            <h2 className="mb-0 fw-bold text-success">
                                                {(summary.deposits?.total || 0).toLocaleString('en-IN')}
                                            </h2>
                                            <span className="badge bg-success mt-2">
                                                {summary.deposits?.count || 0} transactions
                                            </span>
                                        </div>
                                        <div className="stats-icon stats-icon-success">
                                            <i className="icofont-arrow-down"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 col-md-6 col-sm-12">
                            <div className="card h-100 stats-card stats-card-primary">
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <div className="text-muted small text-uppercase mb-2 fw-semibold">Total Withdrawals</div>
                                            <h2 className="mb-0 fw-bold text-primary">
                                                {(summary.withdrawals?.total || 0).toLocaleString('en-IN')}
                                            </h2>
                                            <span className="badge bg-primary mt-2">
                                                {summary.withdrawals?.count || 0} transactions
                                            </span>
                                        </div>
                                        <div className="stats-icon stats-icon-primary">
                                            <i className="icofont-arrow-up"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="card mb-4 filter-card">
                    <div className="card-header bg-transparent border-bottom py-3">
                        <h6 className="mb-0 fw-bold d-flex align-items-center">
                            <i className="icofont-filter me-2 text-primary"></i>
                            Filter & Sort
                        </h6>
                    </div>
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-3">
                                <label className="form-label fw-semibold">Transaction Type</label>
                                <select
                                    className="form-select form-select-lg"
                                    name="type"
                                    value={filters.type}
                                    onChange={handleFilterChange}
                                >
                                    <option value="">All Types</option>
                                    <option value="deposit">Deposits</option>
                                    <option value="withdrawal">Withdrawals</option>
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label fw-semibold">Status</label>
                                <select
                                    className="form-select form-select-lg"
                                    name="status"
                                    value={filters.status}
                                    onChange={handleFilterChange}
                                >
                                    <option value="">All Status</option>
                                    <option value="pending">Pending</option>
                                    <option value="completed">Completed</option>
                                    <option value="failed">Failed</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label fw-semibold">Sort By</label>
                                <select
                                    className="form-select form-select-lg"
                                    name="sortBy"
                                    value={filters.sortBy}
                                    onChange={handleFilterChange}
                                >
                                    <option value="createdAt">Date</option>
                                    <option value="amount">Amount</option>
                                    <option value="status">Status</option>
                                </select>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label fw-semibold">Sort Order</label>
                                <select
                                    className="form-select form-select-lg"
                                    name="sortOrder"
                                    value={filters.sortOrder}
                                    onChange={handleFilterChange}
                                >
                                    <option value="desc">Descending</option>
                                    <option value="asc">Ascending</option>
                                </select>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label fw-semibold">Items Per Page</label>
                                <select
                                    className="form-select form-select-lg"
                                    name="limit"
                                    value={filters.limit}
                                    onChange={handleFilterChange}
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Transactions Table */}
                <div className="card table-card">
                    <div className="card-header py-3 bg-transparent border-bottom">
                        <h6 className="mb-0 fw-bold d-flex align-items-center">
                            <i className="icofont-list me-2 text-primary"></i>
                            Transaction History
                            {transactions.length > 0 && (
                                <span className="badge bg-primary ms-2">{transactions.length}</span>
                            )}
                        </h6>
                    </div>
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="text-muted mt-3">Loading transactions...</p>
                            </div>
                        ) : transactions.length > 0 ? (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th className="fw-semibold">#</th>
                                                <th className="fw-semibold">Date</th>
                                                <th className="fw-semibold">Reference</th>
                                                <th className="fw-semibold">Type</th>
                                                <th className="fw-semibold">Amount</th>
                                                <th className="fw-semibold">Medium</th>
                                                <th className="fw-semibold">Status</th>
                                                <th className="fw-semibold">Description</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transactions.map((transaction, index) => (
                                                <tr key={transaction._id || transaction.id || index} className="trade-row">
                                                    <td>
                                                        <strong>
                                                            {pagination ? (pagination.page - 1) * pagination.limit + index + 1 : index + 1}
                                                        </strong>
                                                    </td>
                                                    <td className="text-muted">{formatDate(transaction.createdAt)}</td>
                                                    <td>
                                                        <strong>{transaction.reference || 'N/A'}</strong>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${
                                                            transaction.type === 'deposit' ? 'bg-success' : 'bg-primary'
                                                        }`}>
                                                            {transaction.type ? transaction.type.toUpperCase() : 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td className="fw-medium">
                                                        {(transaction.amount || 0).toLocaleString('en-IN')}
                                                    </td>
                                                    <td className="text-muted">{transaction.medium || 'N/A'}</td>
                                                    <td>
                                                        {getStatusBadge(transaction.status)}
                                                    </td>
                                                    <td className="text-muted">{transaction.description || 'N/A'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {pagination && pagination.pages > 1 && (
                                    <div className="card-footer bg-transparent border-top py-3">
                                        <div className="d-sm-flex text-center justify-content-between align-items-center">
                                            <div className="dataTables_info mb-2 mb-sm-0">
                                                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                                                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                                                {pagination.total} entries
                                            </div>
                                            <nav aria-label="Page navigation">
                                                <ul className="pagination justify-content-center mb-0">
                                                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => handlePageChange(1)}
                                                            disabled={pagination.page === 1}
                                                        >
                                                            <i className="icofont-arrow-left"></i> First
                                                        </button>
                                                    </li>
                                                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => handlePageChange(pagination.page - 1)}
                                                            disabled={pagination.page === 1}
                                                        >
                                                            <i className="icofont-arrow-left"></i> Previous
                                                        </button>
                                                    </li>
                                                    {[...Array(pagination.pages)].map((_, i) => {
                                                        const pageNum = i + 1;
                                                        if (
                                                            pageNum === 1 ||
                                                            pageNum === pagination.pages ||
                                                            (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2)
                                                        ) {
                                                            return (
                                                                <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                                                                    <button
                                                                        className="page-link"
                                                                        onClick={() => handlePageChange(pageNum)}
                                                                    >
                                                                        {pageNum}
                                                                    </button>
                                                                </li>
                                                            );
                                                        } else if (pageNum === pagination.page - 3 || pageNum === pagination.page + 3) {
                                                            return (
                                                                <li key={pageNum} className="page-item disabled">
                                                                    <span className="page-link">...</span>
                                                                </li>
                                                            );
                                                        }
                                                        return null;
                                                    })}
                                                    <li className={`page-item ${pagination.page === pagination.pages ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => handlePageChange(pagination.page + 1)}
                                                            disabled={pagination.page === pagination.pages}
                                                        >
                                                            Next <i className="icofont-arrow-right"></i>
                                                        </button>
                                                    </li>
                                                    <li className={`page-item ${pagination.page === pagination.pages ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => handlePageChange(pagination.pages)}
                                                            disabled={pagination.page === pagination.pages}
                                                        >
                                                            Last <i className="icofont-arrow-right"></i>
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-5">
                                <div className="empty-state">
                                    <i className="icofont-file-text fs-1 text-muted mb-3 d-block" style={{ fontSize: '4rem', opacity: 0.3 }}></i>
                                    <h5 className="text-muted mb-2">No Transactions Found</h5>
                                    <p className="text-muted mb-0">Your transaction history will appear here</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style>{`
                /* Statistics Cards */
                .stats-card {
                    border: none;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                    transition: all 0.3s ease;
                    overflow: hidden;
                    position: relative;
                }
                .stats-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    background: linear-gradient(90deg, var(--card-color, #007bff), var(--card-color-light, #66b3ff));
                }
                .stats-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
                }
                .stats-card-primary::before {
                    background: linear-gradient(90deg, #007bff, #66b3ff);
                }
                .stats-card-success::before {
                    background: linear-gradient(90deg, #28a745, #71dd8a);
                }
                .stats-icon {
                    width: 60px;
                    height: 60px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 2rem;
                    opacity: 0.15;
                }
                .stats-icon-primary {
                    background: rgba(0, 123, 255, 0.1);
                    color: #007bff;
                }
                .stats-icon-success {
                    background: rgba(40, 167, 69, 0.1);
                    color: #28a745;
                }

                /* Filter Card */
                .filter-card {
                    border: none;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                }
                .filter-card .form-select-lg {
                    border-radius: 8px;
                    border: 1px solid #e0e0e0;
                    transition: all 0.3s ease;
                }
                .filter-card .form-select-lg:focus {
                    border-color: #007bff;
                    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.1);
                }

                /* Table Card */
                .table-card {
                    border: none;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                    overflow: hidden;
                }
                .table-card .table {
                    margin-bottom: 0;
                }
                .table-card .table thead th {
                    background-color: #f8f9fa;
                    border-bottom: 2px solid #dee2e6;
                    padding: 1rem;
                    font-size: 0.875rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    color: #495057;
                }
                .table-card .table tbody td {
                    padding: 1rem;
                    vertical-align: middle;
                    border-color: #f0f0f0;
                }
                .table-card .table tbody tr.trade-row {
                    transition: all 0.2s ease;
                }
                .table-card .table tbody tr.trade-row:hover {
                    background-color: #f8f9fa;
                    transform: scale(1.01);
                }

                /* Pagination */
                .pagination .page-link {
                    border-radius: 8px;
                    margin: 0 2px;
                    border: 1px solid #e0e0e0;
                    color: #495057;
                    transition: all 0.3s ease;
                }
                .pagination .page-link:hover {
                    background-color: #007bff;
                    border-color: #007bff;
                    color: #fff;
                    transform: translateY(-2px);
                }
                .pagination .page-item.active .page-link {
                    background-color: #007bff;
                    border-color: #007bff;
                    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                /* Empty State */
                .empty-state {
                    padding: 3rem 1rem;
                }

                @media (max-width: 768px) {
                    .stats-card {
                        margin-bottom: 1rem;
                    }
                    .table-card .table {
                        font-size: 0.875rem;
                    }
                    .table-card .table thead th,
                    .table-card .table tbody td {
                        padding: 0.75rem 0.5rem;
                    }
                }
            `}</style>
        </>
    );
}

const mapStateToProps = (state) => ({
    auth: state.auth || {},
});

export default connect(mapStateToProps)(TransactionPage);
